import React from 'react'

import { useActiveDevice } from '@/hooks/useActiveDevice'
import { isYoutubeUrl, SectionBackgroundGroupProps } from '@/shared/builder/default/section.default'

import { EffectBackground, MaskBackground, VideoBackground, YoutubeBackground } from './backgrounds'
import { BackgroundLayerStyle } from './types'

/**
 * BackgroundLayer - Main component that renders different types of background layers
 *
 * This component has been refactored from a 2656-line monolithic file into a modular structure:
 * - backgrounds/ - Contains VideoBackground, YoutubeBackground, MaskBackground, EffectBackground
 * - effects/ - Contains 23 individual effect components
 * - hooks/ - Contains useYouTubeAPI hook
 * - utils/ - Contains effectKeyframes utility
 * - types.ts - Contains all TypeScript interfaces
 */
export const BackgroundLayer = React.memo(
  ({
    layerStyle,
    index,
    backgroundSettings,
    videoSrc,
    containerRef,
  }: {
    layerStyle: BackgroundLayerStyle
    index: number
    backgroundSettings: SectionBackgroundGroupProps[]
    videoSrc?: string
    containerRef: React.RefObject<HTMLDivElement>
  }) => {
    const item = backgroundSettings[index]
    const [device] = useActiveDevice()

    // Handle video background
    if (item?.type === 'video' && videoSrc) {
      return isYoutubeUrl(videoSrc) ? (
        <YoutubeBackground
          videoSrc={videoSrc}
          layerStyle={layerStyle}
          index={index}
          values={item.values}
          device={device}
          containerRef={containerRef}
        />
      ) : (
        <VideoBackground videoSrc={videoSrc} layerStyle={layerStyle} index={index} />
      )
    }

    // Handle effect background
    if (item?.type === 'effect') {
      return <EffectBackground layerStyle={layerStyle} index={index} values={item.values} />
    }

    // Handle mask background
    const curValue = (item?.values as Record<string, any>)?.[device]
    if (item?.type === 'mask' && curValue?.type !== '#') {
      return <MaskBackground layerStyle={layerStyle} index={index} values={curValue} />
    }

    // Default background layer
    return layerStyle && Object.keys(layerStyle).length > 0 ? (
      <div style={{ ...layerStyle, zIndex: -index - 1 }} />
    ) : null
  }
)
BackgroundLayer.displayName = 'BackgroundLayer'
